export enum PlanSlug {
  FREE = 'free',
  STARTER = 'starter',
  BUILDER = 'builder',
  SCALE = 'scale',
}

export interface PlanValue {
  value?: string;
  subtext?: string;
  comingSoon?: boolean;
}

export interface CatalogCellData {
  section?: string;
  /** @deprecated fallback only — use `sectionRank` / `compareOrder` / `cardOrder`. */
  sectionOrder?: number;
  /** Rank of this cell's section (card grouping), 1..7. */
  sectionRank?: number;
  /** Row order in the compare table. */
  compareOrder?: number;
  /** Order within a section on the card. */
  cardOrder?: number;
  /**
   * Internal marker: the child that represents its group on the card. Its
   * effect is already applied to the group's card cell, so the FE can ignore it.
   */
  cardPrimary?: boolean;
  code?: string;
  parent?: string;
  group?: string;
  order?: number;
  count?: number;
  countTotal?: number;
  /** Value unit, e.g. "API Requests per Second". */
  unit?: string;
  /** Group counter noun, e.g. "filters" (default "features"). */
  countUnit?: string;
  origin?: string;
  accessLevel?: string;
  value?: string;
  valuesPerPlan?: PlanValue;
  unlimited?: boolean;
  comingSoon?: boolean;
  scalable?: boolean;
  cardOnly?: boolean;
  disabled?: boolean;
  badge?: string;
  tooltip?: string;
  extra?: Record<string, string>;
}

export interface CatalogFeatureChild {
  id: string;
  key: string;
  name: string;
  /**
   * Per-plan cells keyed by the grouped `product.name` (same key space as the
   * parent feature's `cells`). Replaces the old single `cell` so a collapsible
   * child can carry a different value/flag per plan column.
   */
  cells: Record<string, CatalogCellData>;
  products: string[];
}

export interface CatalogFeature {
  key: string;
  name: string;
  metadata: CatalogCellData;
  cells: Record<string, CatalogCellData>;
  children?: CatalogFeatureChild[];
  products: string[];
  productCounts: Record<string, number>;
}

export interface CatalogProductMetadata {
  is_legacy?: string;
  is_public?: string;
  family?: string;
  label?: string;
  order?: string;
  slug?: PlanSlug | string;
  test_product_notif?: string;
  tier?: string;
  tooltip?: string;
}

export interface CatalogProduct {
  id: string;
  name: string;
  description?: string;
  active: boolean;
  marketingFeatures?: string[];
  metadata: CatalogProductMetadata;
}

/** `return=features` — flat compare-table response. */
export interface FeatureCatalog {
  features: CatalogFeature[];
  products: CatalogProduct[];
}

/** Alias for the compare-table response shape (`return=features`). */
export type ProductFeaturesTable = FeatureCatalog;

/** A card section: features already ordered by `cardOrder`; children are `[]`. */
export interface CatalogSection {
  name: string;
  sectionRank: number;
  features: CatalogFeature[];
}

/** `return=cards` — nested-by-section card response. */
export interface ProductCards {
  /** Ordered by `sectionRank`. */
  sections: CatalogSection[];
  /** The plan columns (same set as the compare response). */
  products: CatalogProduct[];
}
